import React, { useState } from 'react';
import './GameOver.css';
import { House, RotateCcw, Share, Send, Trophy, Skull, LogIn } from 'lucide-react'; 
import { useTranslation } from 'react-i18next';
import { useAuth } from '../utils/AuthContext';
import { useAudio } from '../utils/AudioContext'; // Import AudioContext
import { 
    playUiHoverSound, 
    playUiClickSound, 
    playUiConfirmSound, 
    playUiCloseSound 
} from '../utils/sounds'; // Import sounds

function GameOver({ score, collectedCoins = 0, totalTime, moves, onRestart, onQuit, gameMode, challengeData, onOpenLogin }) {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const { sfxMuted } = useAudio(); // Get audio state

  const [showPopup, setShowPopup] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [exitAction, setExitAction] = useState(null);

  const isChallengeWin = gameMode === 'attempt_challenge' && challengeData && score > challengeData.scoreToBeat;

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };

  const handleShare = async () => {
    playUiClickSound(sfxMuted); // Share is a click action
    const shareData = {
      title: 'Tile Sprint',
      text: `I just scored ${score} in Tile Sprint! Can you beat it?`,
      url: 'https://www.tile-sprint.com',
    };

    const clipboardText = `I just scored ${score} in Tile Sprint! Can you beat it? Play Tile Sprint: https://www.tile-sprint.com`;

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        console.error("Share failed:", err);
      }
    } else {
      navigator.clipboard.writeText(clipboardText).then(() => {
        setShowPopup(true);
        setTimeout(() => {
          setShowPopup(false);
        }, 2000); 
      });
    }
  };

  const handleRestartClick = () => {
    playUiClickSound(sfxMuted); // Restart is a click action
    setExitAction(() => onRestart);
    setIsExiting(true);
  };

  const handleQuitClick = () => {
    playUiCloseSound(sfxMuted); // Quit/Home is a close action
    setExitAction(() => onQuit);
    setIsExiting(true);
  };

  const handleAnimationEnd = () => {
    if (isExiting && exitAction) {
      exitAction();
    }
  };

  // --- RENDER CONTENT BASED ON MODE ---
  const renderContent = () => {
    // 1. CREATING CHALLENGE
    if (gameMode === 'create_challenge') {
        return (
            <>
                <div style={{ marginBottom: '20px', color: '#fbbf24' }}>
                    <Send size={48} strokeWidth={2.5} />
                </div>
                <h1 className="modal-title" style={{ fontSize: '2rem' }}>CHALLENGE SENT!</h1>
                <p style={{ color: '#9ca3af', marginBottom: '30px', fontWeight: '600' }}>
                    You set a target of <span style={{ color: '#fbbf24', fontSize: '1.2rem' }}>{score}</span>
                    <br/>for your friend to beat.
                </p>
                
                <div className="stats-container">
                    <div className="secondary-stats" style={{ borderTop: 'none', paddingTop: 0 }}>
                        <div className="stat-box">
                            <span className="stat-value">{moves}</span>
                            <span className="stat-label">Moves</span>
                        </div>
                        <div className="stat-box">
                            <span className="stat-value" style={{color: '#ffd700'}}>{collectedCoins}</span>
                            <span className="stat-label">Coins</span>
                        </div>
                        <div className="stat-box">
                            <span className="stat-value">{formatTime(totalTime)}</span>
                            <span className="stat-label">Time</span>
                        </div>
                    </div>
                </div>

                <div className="buttons-container">
                    <button 
                        className="btn btn-red" 
                        onClick={handleQuitClick} 
                        onMouseEnter={() => playUiHoverSound(sfxMuted)}
                        style={{width: '80px', height: '80px'}}
                    >
                        <House size={32} strokeWidth={3} />
                    </button>
                </div>
            </>
        );
    }

    // 2. ATTEMPTING CHALLENGE
    if (gameMode === 'attempt_challenge') {
        return (
            <>
                <div style={{ marginBottom: '20px', color: isChallengeWin ? '#22c55e' : '#ef4444' }}>
                    {isChallengeWin ? <Trophy size={56} /> : <Skull size={56} />}
                </div>
                <h1 className="modal-title" style={{ fontSize: '2.2rem', color: isChallengeWin ? '#4ade80' : '#f87171' }}>
                    {isChallengeWin ? 'YOU WON!' : 'DEFEAT'}
                </h1>
                <p style={{ color: '#e5e7eb', marginBottom: '20px', fontWeight: '600' }}>
                    {isChallengeWin 
                        ? `You beat the target of ${challengeData.scoreToBeat}!` 
                        : `You failed to beat ${challengeData.scoreToBeat}.`
                    }
                </p>

                <div className="stats-container">
                    <div className="main-stat">
                        <span className="stat-label">YOUR SCORE</span>
                        <span className="main-value">{score}</span>
                    </div>
                    <div className="secondary-stats">
                        <div className="stat-box">
                             <span className="stat-value" style={{color: '#ffd700'}}>{collectedCoins}</span>
                             <span className="stat-label">Coins</span>
                        </div>
                    </div>
                </div>

                <div className="buttons-container">
                     <button 
                        className="btn btn-red" 
                        onClick={handleQuitClick} 
                        onMouseEnter={() => playUiHoverSound(sfxMuted)}
                        style={{width: '80px', height: '80px'}}
                    >
                        <House size={32} strokeWidth={3} />
                    </button>
                </div>
            </>
        );
    }

    // 3. STANDARD GAME OVER
    return (
        <>
            <h1 className="modal-title">
                {t('game_over').split('').map((char, index) => {
                    const colors = ['c-green', 'c-blue', 'c-red', 'c-yellow'];
                    const color = colors[index % colors.length];
                    return <span key={index} className={color}>{char}</span>;
                })}
            </h1>

            <div className="stats-container">
                <div className="main-stat">
                    <span className="stat-label">{t('total_score')}</span>
                    <span className="main-value">{score}</span>
                </div>

                <div className="secondary-stats">
                    <div className="stat-box">
                        <span className="stat-value">{moves}</span>
                        <span className="stat-label">{t('moves')}</span>
                    </div>
                    <div className="stat-box">
                        <span className="stat-value" style={{color: '#ffd700'}}>{collectedCoins}</span>
                        <span className="stat-label">Coins</span>
                    </div>
                    <div className="stat-box">
                        <span className="stat-value">{formatTime(totalTime)}</span>
                        <span className="stat-label">{t('time')}</span>
                    </div>
                </div>
            </div>

            {/* GUEST INCENTIVE HOOK */}
            {!currentUser && (
                <div className="guest-alert-box">
                    <p className="guest-alert-text">
                        Playing as Guest. Score will not be saved!
                    </p>
                    <button 
                        className="save-score-btn" 
                        onClick={() => {
                            playUiConfirmSound(sfxMuted); // Sign up is a confirm action
                            onOpenLogin();
                        }}
                        onMouseEnter={() => playUiHoverSound(sfxMuted)}
                    >
                        <LogIn size={18} style={{marginRight: '8px'}} />
                        SIGN UP TO SAVE
                    </button>
                </div>
            )}

            <div className="buttons-container">
                <button 
                    className="btn btn-red" 
                    aria-label={t('home')} 
                    onClick={handleQuitClick}
                    onMouseEnter={() => playUiHoverSound(sfxMuted)}
                >
                    <House size={28} strokeWidth={3} />
                </button>
                <button 
                    className="btn btn-green" 
                    aria-label={t('replay')} 
                    onClick={handleRestartClick}
                    onMouseEnter={() => playUiHoverSound(sfxMuted)}
                >
                    <RotateCcw size={40} strokeWidth={3} />
                </button>
                <button 
                    className="btn btn-blue" 
                    aria-label={t('share')} 
                    onClick={handleShare}
                    onMouseEnter={() => playUiHoverSound(sfxMuted)}
                >
                    <Share size={28} strokeWidth={3} />
                </button>
            </div>
        </>
    );
  };

  return (
    <div className={`game-over-container ${isExiting ? 'fade-out' : ''}`}>
      <div 
        className={`game-over-modal ${isExiting ? 'pop-out' : ''}`}
        onAnimationEnd={handleAnimationEnd}
      >
        {renderContent()}
        {showPopup && <div className="share-popup">{t('copied_to_clipboard')}</div>}
      </div>
    </div>
  );
}

export default GameOver;